import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { clearDevicePush, currentBinding, decodePublicKey, rememberBinding, sendToWorker, syncPushBadge } from '../lib/pushClient';
import './assetExport.css';
const API = process.env.REACT_APP_BACKEND_URL + '/api';
const Context = createContext(null);

export const PushNotificationsProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [ready, setReady] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preferences, setPreferences] = useState(false);
  const [permission, setPermission] = useState('Notification' in window ? Notification.permission : 'denied');
  const config = useRef(null);
  const pending = useRef(false);
  const readyRef = useRef(false);
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const installed = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
  const supported = window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && (!isIos || installed);
  useEffect(() => {
    if (loading) return;
    let active = true;
    let retryOnGesture = null;
    const prepare = async () => {
      config.current = null;
      setPreferences(false);
      setReady(false); readyRef.current = false; setError('');
      if (!user) { await clearDevicePush(); if (active) setEnabled(false); return; }
      if (!supported) return;
      setPermission(Notification.permission);
      const previous = currentBinding();
      if (previous && previous.userId !== user.id) await clearDevicePush();
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/config`, { withCredentials: true });
      if (!active) return;
      config.current = { registration, key: data.public_key, userId: user.id };
      setReady(true); readyRef.current = true;
      const existing = currentBinding();
      if (Notification.permission === 'granted') {
        // El permiso del navegador ya está concedido: se reconecta siempre,
        // aunque no haya un registro local previo (ej. localStorage se
        // perdió, o es la primera vez que se abre en este dispositivo tras
        // concederlo desde otro contexto). No depender de ese registro es
        // lo que garantiza que la elección persista entre cierres de la app.
        if (existing && existing.userId !== user.id) await clearDevicePush();
        try { await attach(); }
        catch (err) { if (active) setError(err.message || 'No se pudo reactivar las notificaciones en este dispositivo.'); }
      } else {
        if (existing) await clearDevicePush();
        setEnabled(false);
      }
      // Primera vez que este usuario abre la app en este dispositivo: se pide
      // el permiso del sistema directamente, sin un diálogo propio antes.
      if (Notification.permission === 'default' && !localStorage.getItem('xenicos.push.prompted.' + user.id)) {
        localStorage.setItem('xenicos.push.prompted.' + user.id, '1');
        activate();
        // Safari en iOS sólo concede el permiso si la llamada ocurre dentro
        // de un gesto del usuario: si el intento inmediato no resolvió nada
        // (sigue en 'default'), reintentamos en el primer toque/clic.
        retryOnGesture = () => { if (Notification.permission === 'default') activate(); };
        document.addEventListener('pointerdown', retryOnGesture, { once: true, capture: true });
      }
    };
    prepare().catch(() => { if (active) setError('No se pudieron preparar las notificaciones. Vuelve a intentarlo al abrir la app.'); });
    const refresh = () => {
      if ('Notification' in window) setPermission(Notification.permission);
      if (user && currentBinding()?.userId === user.id) {
        if ('Notification' in window && Notification.permission !== 'granted') {
          clearDevicePush().then(() => setEnabled(false)).catch(() => {});
        } else syncPushBadge().catch(() => {});
      }
    };
    window.addEventListener('xenicos:refresh-pendientes', refresh);
    window.addEventListener('xenicos:notifications-refreshed', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      if (retryOnGesture) document.removeEventListener('pointerdown', retryOnGesture, { capture: true });
      window.removeEventListener('xenicos:refresh-pendientes', refresh);
      window.removeEventListener('xenicos:notifications-refreshed', refresh);
      window.removeEventListener('focus', refresh);
    };
    // attach uses the session/config captured and checked above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, supported]);
  const attach = async () => {
    const setup = config.current;
    if (!setup) throw new Error('No disponible');
    let subscription = await setup.registration.pushManager.getSubscription();
    if (subscription?.options.applicationServerKey) {
      const expected = decodePublicKey(setup.key);
      const current = new Uint8Array(subscription.options.applicationServerKey);
      if (expected.length !== current.length || expected.some((byte, i) => byte !== current[i])) {
        await subscription.unsubscribe(); subscription = null;
      }
    }
    if (!subscription) {
      try {
        subscription = await setup.registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: decodePublicKey(setup.key) });
      } catch {
        throw new Error('El navegador no permitió activar las notificaciones en este dispositivo.');
      }
    }
    const { data } = await axios.put(`${API}/push/subscription`, subscription.toJSON(), { withCredentials: true });
    // Una respuesta anterior no debe limpiar el dispositivo de la nueva sesión.
    if (data.user_id !== setup.userId || config.current !== setup) return;
    rememberBinding({ userId: setup.userId, binding: data.binding });
    await sendToWorker({ type: 'BIND_PUSH', binding: data.binding, expiresAt: data.expires_at, count: data.count, countAt: data.count_at });
    if (config.current !== setup || currentBinding()?.binding !== data.binding) return;
    await axios.post(`${API}/push/ready/${encodeURIComponent(data.binding)}`, {}, { withCredentials: true });
    if (config.current !== setup || currentBinding()?.binding !== data.binding) return;
    setEnabled(true);
  };
  const dismiss = () => { if (user) localStorage.setItem('xenicos.push.prompted.' + user.id, '1'); };
  const activate = async () => {
    if (pending.current || !readyRef.current) return;
    pending.current = true; setBusy(true); setError('');
    try {
      // La petición nativa se ejecuta dentro del gesto, antes de cualquier await de red.
      let permission;
      try { permission = await Notification.requestPermission(); }
      catch { throw new Error('El navegador no permitió solicitar el permiso de notificaciones.'); }
      setPermission(permission);
      if (permission !== 'granted') throw new Error('Permiso no concedido. Puedes cambiarlo en los ajustes del navegador o del dispositivo.');
      await attach(); dismiss();
    } catch (err) {
      const detail = err.response ? (err.response.data?.detail || 'No se pudo completar la solicitud. Inténtalo de nuevo.') : (err.message || 'No se pudieron activar las notificaciones.');
      setError(detail);
    }
    finally { pending.current = false; setBusy(false); }
  };
  const disable = async () => {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    try { await clearDevicePush(); setEnabled(false); dismiss(); }
    finally { pending.current = false; setBusy(false); }
  };
  const context = { supported, isIos, installed, ready, enabled, busy, error, activate, disable, permission,
    openPreferences: () => { setPreferences(true); } };
  return <Context.Provider value={context}>{children}{user && preferences && <PushPreferences dismiss={() => setPreferences(false)} />}</Context.Provider>;
};

const PushPreferences = ({ dismiss }) => {
  const ref = useRef(null);
  const state = useContext(Context);
  useEffect(() => { const dialog = ref.current; dialog.showModal(); return () => dialog.close(); }, []);
  return <dialog ref={ref} className="asset-export-dialog" aria-label="Notificaciones en este dispositivo" data-testid="push-preferences-dialog" onCancel={event => { event.preventDefault(); if (!state.busy) dismiss(); }}>
    <PushNotificationsCard testPrefix="push-preferences-form" bordered={false} />
    <button className="btn-secondary self-end" disabled={state.busy} onClick={dismiss} data-testid="push-preferences-close">Cerrar</button>
  </dialog>;
};

export const PushSettingsButton = ({ onOpen }) => {
  const context = useContext(Context);
  if (!context) return null;
  return <button className="w-full border-t border-slate-200 p-3 text-sm text-left hover:bg-slate-50 flex items-center gap-2" onClick={() => { onOpen?.(); context.openPreferences(); }} data-testid="notification-push-settings">
    <Bell className="w-4 h-4" />Notificaciones en este dispositivo
  </button>;
};

export const PushNotificationsCard = ({ testPrefix = 'push-settings', bordered = true }) => {
  const state = useContext(Context);
  if (!state) return null;
  return <section className={`py-5 space-y-3 ${bordered ? 'border-t border-slate-200' : ''}`} data-testid={testPrefix}>
    <h2 className="text-base md:text-lg font-semibold">Notificaciones en este dispositivo</h2>
    <p className="text-sm" data-testid={`${testPrefix}-status`}>{!state.supported ? (state.isIos ? 'Requiere iOS 16.4 o superior y la app instalada desde Safari.' : 'Este navegador no admite Web Push.') : state.permission === 'denied' ? 'Permiso bloqueado. Puedes activarlo en los ajustes del navegador o del dispositivo.' : state.enabled ? 'Activadas para tu sesión actual' : 'Desactivadas'}</p>
    <p className="text-xs text-slate-500" data-testid={`${testPrefix}-badge-compatibility`}>Solo actualizan el número en el icono de la app, sin texto ni enlace a ningún activo. El número exacto depende del sistema: Android puede mostrar sólo un punto.</p>
    {state.error && <p role="alert" className="text-sm text-red-600" data-testid={`${testPrefix}-error`}>{state.error}</p>}
    {state.supported && <button className="btn-primary inline-flex items-center gap-2" disabled={!state.ready || state.busy || (!state.enabled && state.permission === 'denied')} onClick={state.enabled ? state.disable : state.activate} data-testid={`${testPrefix}-toggle`}>
      {state.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : state.enabled ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}{state.enabled ? 'Desactivar' : 'Activar notificaciones'}
    </button>}
  </section>;
};